import { supabase } from '@/shared/config/supabase';
import type { IBrandingRepository } from '../../domain/repositories/IBrandingRepository';
import type { BrandingConfigDTO } from '../../application/dto/BrandingConfigDTO';
import type { FontFamily, FontWeight, CustomDomainStatus } from '../../domain/entities/BrandingConfig';

interface BrandingRow {
  id: number;
  workspace_id: number;
  primary_color: string;
  secondary_color: string;
  text_color: string;
  background_color: string;
  font_family: string;
  font_size: number;
  font_weight: string;
  line_height: number;
  letter_spacing: number;
  border_radius: number;
  company_name: string | null;
  tagline: string | null;
  logo_light_url: string | null;
  logo_dark_url: string | null;
  favicon_url: string | null;
  preset_id: string | null;
  show_powered_by: boolean | null;
  custom_domain: string | null;
  domain_status: string | null;
  domain_verified_at: string | null;
  domain_verification_token: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * Supabase implementation of branding repository
 * Handles persistence of workspace branding configuration
 */
export class SupabaseBrandingRepository implements IBrandingRepository {
  async getBranding(workspaceId: number): Promise<BrandingConfigDTO | null> {
    const { data, error } = await supabase
      .from('workspace_branding')
      .select('*')
      .eq('workspace_id', workspaceId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows found
        return null;
      }
      throw new Error(`Failed to fetch branding: ${error.message}`);
    }

    if (!data) {
      return null;
    }

    return this.mapToDTO(data);
  }

  async saveBranding(workspaceId: number, branding: Partial<BrandingConfigDTO>): Promise<BrandingConfigDTO> {
    const dbData = this.mapToDbFormat(workspaceId, branding);

    const { data, error } = await supabase
      .from('workspace_branding')
      .upsert(dbData, { onConflict: 'workspace_id' })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to save branding: ${error.message}`);
    }

    return this.mapToDTO(data);
  }

  async uploadLogo(workspaceId: number, file: File, type: 'light' | 'dark' | 'favicon'): Promise<string> {
    const fileExt = file.name.split('.').pop();
    const fileName = `${workspaceId}/logo-${type}.${fileExt}`;
    const bucket = 'workspace-branding';

    // Upload with upsert to replace existing file
    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(fileName, file, { upsert: true });

    if (uploadError) {
      throw new Error(`Failed to upload logo: ${uploadError.message}`);
    }

    // Get public URL
    const { data } = supabase.storage.from(bucket).getPublicUrl(fileName);
    return data.publicUrl;
  }

  async deleteLogo(workspaceId: number, type: 'light' | 'dark' | 'favicon'): Promise<void> {
    const bucket = 'workspace-branding';

    // List files in workspace directory
    const { data: files, error: listError } = await supabase.storage
      .from(bucket)
      .list(`${workspaceId}`);

    if (listError) {
      throw new Error(`Failed to list logos: ${listError.message}`);
    }

    // Find the logo file with matching type
    const logoFile = files?.find(f => f.name.startsWith(`logo-${type}`));
    if (logoFile) {
      const { error: deleteError } = await supabase.storage
        .from(bucket)
        .remove([`${workspaceId}/${logoFile.name}`]);

      if (deleteError) {
        throw new Error(`Failed to delete logo: ${deleteError.message}`);
      }
    }
  }

  async updateCustomDomain(
    workspaceId: number,
    domain: string | null,
    status: CustomDomainStatus,
    verificationToken?: string
  ): Promise<BrandingConfigDTO> {
    const updateData: Record<string, unknown> = {
      custom_domain: domain,
      domain_status: status,
    };

    if (verificationToken !== undefined) {
      updateData.domain_verification_token = verificationToken;
    }

    if (status === 'active') {
      updateData.domain_verified_at = new Date().toISOString();
    }

    if (domain === null) {
      updateData.domain_verified_at = null;
      updateData.domain_verification_token = null;
      updateData.domain_status = 'none';
    }

    const { data, error } = await supabase
      .from('workspace_branding')
      .update(updateData)
      .eq('workspace_id', workspaceId)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update custom domain: ${error.message}`);
    }

    return this.mapToDTO(data);
  }

  async isDomainTaken(domain: string, excludeWorkspaceId?: number): Promise<boolean> {
    let query = supabase
      .from('workspace_branding')
      .select('workspace_id')
      .eq('custom_domain', domain);

    if (excludeWorkspaceId !== undefined) {
      query = query.neq('workspace_id', excludeWorkspaceId);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(`Failed to check domain availability: ${error.message}`);
    }

    return (data?.length ?? 0) > 0;
  }

  /**
   * Map database row to DTO
   */
  private mapToDTO(data: BrandingRow): BrandingConfigDTO {
    return {
      id: data.id.toString(),
      workspaceId: data.workspace_id,
      primaryColor: data.primary_color,
      secondaryColor: data.secondary_color,
      textColor: data.text_color,
      backgroundColor: data.background_color,
      fontFamily: data.font_family as FontFamily,
      fontSize: data.font_size,
      fontWeight: data.font_weight as unknown as FontWeight,
      lineHeight: data.line_height,
      letterSpacing: data.letter_spacing,
      borderRadius: data.border_radius,
      companyName: data.company_name ?? undefined,
      tagline: data.tagline ?? undefined,
      logoLightUrl: data.logo_light_url ?? undefined,
      logoDarkUrl: data.logo_dark_url ?? undefined,
      faviconUrl: data.favicon_url ?? undefined,
      presetId: data.preset_id ?? undefined,
      showPoweredBy: data.show_powered_by ?? undefined,
      customDomain: data.custom_domain ?? undefined,
      customDomainStatus: (data.domain_status as CustomDomainStatus) ?? undefined,
      domainVerifiedAt: data.domain_verified_at ?? undefined,
      domainVerificationToken: data.domain_verification_token ?? undefined,
      createdAt: data.created_at ?? undefined,
      updatedAt: data.updated_at ?? undefined,
    };
  }

  /**
   * Map DTO to database format
   */
  private mapToDbFormat(workspaceId: number, branding: Partial<BrandingConfigDTO>): Record<string, unknown> {
    return {
      workspace_id: workspaceId,
      primary_color: branding.primaryColor,
      secondary_color: branding.secondaryColor,
      text_color: branding.textColor,
      background_color: branding.backgroundColor,
      font_family: branding.fontFamily,
      font_size: branding.fontSize,
      font_weight: branding.fontWeight,
      line_height: branding.lineHeight,
      letter_spacing: branding.letterSpacing,
      border_radius: branding.borderRadius,
      company_name: branding.companyName,
      tagline: branding.tagline,
      logo_light_url: branding.logoLightUrl,
      logo_dark_url: branding.logoDarkUrl,
      favicon_url: branding.faviconUrl,
      preset_id: branding.presetId,
      show_powered_by: branding.showPoweredBy,
      custom_domain: branding.customDomain,
      domain_status: branding.customDomainStatus,
      domain_verification_token: branding.domainVerificationToken,
    };
  }
}
